// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ArchiveControl } from '@/components/archive-control';
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { SupportSnackbar } from '@/components/support-snackbar';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import { EditRounded, MergeRounded } from '@/constants/hardfin-standard-icons';
import { updateContact } from '@/contact/api/update-contact';
import { EditContactDialog } from '@/contact/components/edit-contact-dialog';
import { MergeContactDialog } from '@/contact/components/merge-contact-dialog';
import { useContact } from '@/contact/hooks/use-contact';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Breadcrumbs,
  Chip,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

export const Route = createFileRoute('/_app/contacts/$contactKey')({
  component: ContactRoute,
});

function ContactRoute() {
  const { contactKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="contact"
      listPath="/contacts"
      listLabel="Go to contacts"
    >
      <ContactContent contactKey={contactKey} />
    </NotFoundErrorBoundary>
  );
}

function ContactContent(props: { contactKey: string }) {
  const queryClient = useQueryClient();
  const editDialog = useDisclosure();
  const mergeDialog = useDisclosure();

  const { contact } = useContact(props.contactKey);
  const fullName = `${contact.firstName} ${contact.lastName}`;

  const updateContactMutation = useMutation({
    mutationFn: (isArchived: boolean) =>
      updateContact(props.contactKey, {
        isArchived,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact'] });
    },
  });

  const archiveToggleTooltip = contact.isArchived
    ? 'Unarchive contact'
    : 'Archive contact. Archiving does not change your contact, but archived contacts are hidden by default in the contacts list.';

  const handleChangeArchived = (isArchived: boolean) => {
    updateContactMutation.mutate(isArchived);
  };

  return (
    <Container maxWidth="xl">
      <SupportSnackbar
        isOpen={updateContactMutation.isError}
        message="An error occurred while updating the contact"
        onClose={updateContactMutation.reset}
      />
      <EditContactDialog
        isOpen={editDialog.isOpen}
        contact={contact}
        onClose={editDialog.close}
      />
      {/* Conditionally mounted so every piece of dialog state (form values,
          step, selection) is discarded on close; mirrors MergeCustomerDialog */}
      {mergeDialog.isOpen && (
        <MergeContactDialog
          isOpen
          contact={contact}
          onClose={mergeDialog.close}
        />
      )}
      <Stack marginY={3} spacing={3} paddingX={{ xs: 1, md: 3 }}>
        <Stack>
          <Breadcrumbs separator="›">
            <Link
              to="/contacts"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Contacts
            </Link>
            <Typography variant="body2" color="text.secondary">
              {fullName}
            </Typography>
          </Breadcrumbs>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={1}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5">{fullName}</Typography>
              {contact.isArchived && <Chip size="small" label="Archived" />}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip arrow title="Edit contact">
                <IconButton size="small" onClick={editDialog.open}>
                  <EditRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow title="Merge contacts">
                <IconButton size="small" onClick={mergeDialog.open}>
                  <MergeRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="top" title={archiveToggleTooltip}>
                <span>
                  <ArchiveControl
                    isArchived={contact.isArchived}
                    isDisabled={updateContactMutation.isPending}
                    onChange={handleChangeArchived}
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Company</Typography>
            <Link
              to="/companies/$companyKey"
              params={{ companyKey: contact.customer.key }}
              variant="body2"
              color="text.primary"
            >
              {contact.customer.name}
            </Link>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            borderTop="1px solid transparent"
          >
            <VerticalNavigationLink
              to="/contacts/$contactKey"
              params={{ contactKey: props.contactKey }}
              activeOptions={{ exact: true }}
            >
              Details
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/contacts/$contactKey/audit"
              params={{ contactKey: props.contactKey }}
            >
              Audit log
            </VerticalNavigationLink>
          </Stack>
          <Box flexGrow={1} paddingLeft={{ xs: 0, md: 2 }}>
            <SupportErrorBoundary message="An error occurred.">
              <Suspense>
                <Outlet />
              </Suspense>
            </SupportErrorBoundary>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
}
